import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEstablishmentPermissions } from '../../hooks/useEstablishmentPermissions';
import { checkRoutePermission } from '../../utils/permissionUtils';
import { useEstablishment } from '../../contexts/EstablishmentContext';
import { Role } from '../../constants/roles';
import { LoadingSpinner } from '../ui/LoadingSpinner';

// Permission map kept for reference but moved to avoid lint warnings
// Routes are now primarily checked via checkRoutePermission and canViewRoute

const RESIDENT_GOVERNANCE_PATH = '/governanca/condomino';

// Regra de flag isolada: o morador só acessa a governança read-only quando o
// síndico mantém showGovernanceToResidents ligado no estabelecimento corrente.
const isResidentGovernanceBlocked = (
  pathname: string,
  roles: Role[],
  establishment: { showGovernanceToResidents?: boolean } | null,
): boolean =>
  pathname === RESIDENT_GOVERNANCE_PATH &&
  roles.includes(Role.Resident) &&
  establishment?.showGovernanceToResidents === false;

const PermissionWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currentEstablishment } = useEstablishment();
  const {
    currentPermissions,
    currentRoles,
    canViewRoute,
    isLoading
  } = useEstablishmentPermissions();
  
  const [permissionChecked, setPermissionChecked] = useState(false);

  useEffect(() => {
    // console.log('[PermissionWrapper] Permission check:', {
    //   pathname: location.pathname,
    //   isAuthenticated,
    //   isLoading,
    //   permissionsCount: currentPermissions.length,
    //   rolesCount: currentRoles.length,
    //   permissions: currentPermissions.slice(0, 5)
    // });
    
    if (isAuthenticated && !isLoading) {
      // Check permissions using establishment context
      const hasPermission = checkRoutePermission(
        location.pathname, 
        currentRoles as Role[], 
        currentPermissions
      );
      
      // Alternative check using the hook's built-in method
      const canView = canViewRoute(location.pathname);
      
      // console.log('[PermissionWrapper] Route check results:', {
      //   pathname: location.pathname,
      //   hasPermission,
      //   canView,
      //   requiredPermissions: ROUTE_PERMISSION_MAP[location.pathname] || 'none',
      //   userHasDashboardView: currentPermissions.includes('dashboard.view')
      // });
      
      const blocked = isResidentGovernanceBlocked(
        location.pathname,
        currentRoles as Role[],
        currentEstablishment,
      );

      // Use either check (they should align, but this provides flexibility)
      const finalPermission = (hasPermission || canView) && !blocked;

      if (!finalPermission && location.pathname !== '/403') {
        navigate('/403', { replace: true });
      }

      setPermissionChecked(true);
    } else if (!isAuthenticated) {
      // If not authenticated, ProtectedRoute in App.tsx should handle redirection to login
      setPermissionChecked(true);
    }
  }, [location, navigate, currentRoles, currentPermissions, canViewRoute, isAuthenticated, isLoading, currentEstablishment]);

  // Show loading spinner while checking permissions
  if (isLoading || !permissionChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // If authenticated, do a final check before rendering
  if (isAuthenticated && location.pathname !== '/403') {
    const hasPermission =
      checkRoutePermission(
        location.pathname,
        currentRoles as Role[],
        currentPermissions,
      ) &&
      !isResidentGovernanceBlocked(
        location.pathname,
        currentRoles as Role[],
        currentEstablishment,
      );

    if (!hasPermission) {
      // Return null to prevent rendering while navigation happens
      return null;
    }
  }

  return <Outlet />;
};

export default PermissionWrapper;
